import { useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ArrowDown, Send, Mail, Phone, MapPin } from "lucide-react";
import { CV } from "@/data/cv";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Contact() {
  const [form, setForm] = useState({ name: "", email: "", message: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);

  const update = (k) => (e) => {
    setForm((f) => ({ ...f, [k]: e.target.value }));
    if (errors[k]) setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Bitte gib deinen Namen an.";
    if (!form.email.trim()) e.email = "E-Mail-Adresse erforderlich.";
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) e.email = "Ungültige E-Mail-Adresse.";
    if (!form.message.trim() || form.message.trim().length < 10)
      e.message = "Nachricht sollte mindestens 10 Zeichen umfassen.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) return;
    setLoading(true);
    try {
      await axios.post(`${API}/contact`, form);
      setSent(true);
      setForm({ name: "", email: "", message: "" });
      toast.success("Nachricht gesendet — ich melde mich bald!");
    } catch (err) {
      const detail = err?.response?.data?.detail || "Bitte später erneut versuchen.";
      toast.error(`Senden fehlgeschlagen: ${detail}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <section id="kontakt" data-testid="contact-section" className="py-24 md:py-32">
      <div className="max-w-[1400px] mx-auto px-5 md:px-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <p className="font-mono-acc text-xs text-[#525252] mb-4">// 06 — Kontakt</p>
            <h2 className="font-display uppercase text-5xl md:text-6xl lg:text-7xl leading-[0.9]">
              Lass uns<br />
              <span className="text-[#FF3B00]">reden.</span>
            </h2>
            <p className="mt-6 text-base md:text-lg text-[#0F0F0F] max-w-md leading-relaxed">
              Ob Festanstellung, Kollaboration oder Kaffee in Berlin —
              schreib mir eine Nachricht oder lade direkt mein CV herunter.
            </p>

            <div className="mt-10 space-y-3">
              <a
                data-testid="contact-email-link"
                href={`mailto:${CV.email}`}
                className="flex items-center gap-3 group"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 border-2 border-black bg-[#F4F4F0] group-hover:bg-[#FF3B00] group-hover:text-white transition-colors">
                  <Mail className="w-4 h-4" />
                </span>
                <span className="font-mono-acc text-xs md:text-sm link-underline">{CV.email}</span>
              </a>
              <a
                data-testid="contact-phone-link"
                href={`tel:${CV.phone.replace(/\s/g, "")}`}
                className="flex items-center gap-3 group"
              >
                <span className="inline-flex items-center justify-center w-10 h-10 border-2 border-black bg-[#F4F4F0] group-hover:bg-[#FF3B00] group-hover:text-white transition-colors">
                  <Phone className="w-4 h-4" />
                </span>
                <span className="font-mono-acc text-xs md:text-sm link-underline">{CV.phone}</span>
              </a>
              <div className="flex items-center gap-3">
                <span className="inline-flex items-center justify-center w-10 h-10 border-2 border-black bg-[#F4F4F0]">
                  <MapPin className="w-4 h-4" />
                </span>
                <span className="font-mono-acc text-xs md:text-sm">{CV.location}</span>
              </div>
            </div>

            <div className="mt-10">
              <p className="font-mono-acc text-[10px] text-[#525252] mb-3">// Downloads</p>
              <div className="flex flex-col sm:flex-row flex-wrap gap-3">
                <a
                  data-testid="contact-download-cv-de-btn"
                  href={CV.downloads.cvDe.href}
                  download
                  className="inline-flex items-center gap-3 px-5 py-3 bg-black text-[#F4F4F0] border-2 border-black shadow-brutal hover:translate-y-[2px] hover:shadow-brutal-press transition-all font-mono-acc text-[11px]"
                >
                  {CV.downloads.cvDe.label} <ArrowDown className="w-4 h-4" />
                </a>
                <a
                  data-testid="contact-download-cv-en-btn"
                  href={CV.downloads.cvEn.href}
                  download
                  className="inline-flex items-center gap-3 px-5 py-3 bg-[#F4F4F0] text-black border-2 border-black shadow-brutal hover:translate-y-[2px] hover:shadow-brutal-press transition-all font-mono-acc text-[11px]"
                >
                  {CV.downloads.cvEn.label} <ArrowDown className="w-4 h-4" />
                </a>
                <a
                  data-testid="contact-download-portfolio-btn"
                  href={CV.downloads.portfolio.href}
                  download
                  className="inline-flex items-center gap-3 px-5 py-3 bg-[#FF3B00] text-white border-2 border-black shadow-brutal hover:translate-y-[2px] hover:shadow-brutal-press transition-all font-mono-acc text-[11px]"
                >
                  {CV.downloads.portfolio.label} <ArrowDown className="w-4 h-4" />
                </a>
              </div>
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="contact-form"
              className="bg-[#F4F4F0] border-2 border-black shadow-brutal p-6 md:p-10 space-y-6"
            >
              <div>
                <label htmlFor="name" className="font-mono-acc text-[11px] block mb-2">
                  Name *
                </label>
                <input
                  id="name"
                  type="text"
                  data-testid="contact-name-input"
                  value={form.name}
                  onChange={update("name")}
                  className="w-full px-4 py-3 bg-white border-2 border-black focus:outline-none focus:bg-[#FFF6F2] font-medium"
                  placeholder="Wie heißt du?"
                  aria-invalid={!!errors.name}
                />
                {errors.name && (
                  <p data-testid="contact-name-error" className="mt-2 font-mono-acc text-[10px] text-[#FF3B00]">
                    {errors.name}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="email" className="font-mono-acc text-[11px] block mb-2">
                  E-Mail *
                </label>
                <input
                  id="email"
                  type="email"
                  data-testid="contact-email-input"
                  value={form.email}
                  onChange={update("email")}
                  className="w-full px-4 py-3 bg-white border-2 border-black focus:outline-none focus:bg-[#FFF6F2] font-medium"
                  placeholder="du@firma.de"
                  aria-invalid={!!errors.email}
                />
                {errors.email && (
                  <p data-testid="contact-email-error" className="mt-2 font-mono-acc text-[10px] text-[#FF3B00]">
                    {errors.email}
                  </p>
                )}
              </div>

              <div>
                <label htmlFor="message" className="font-mono-acc text-[11px] block mb-2">
                  Nachricht *
                </label>
                <textarea
                  id="message"
                  rows={6}
                  data-testid="contact-message-input"
                  value={form.message}
                  onChange={update("message")}
                  className="w-full px-4 py-3 bg-white border-2 border-black focus:outline-none focus:bg-[#FFF6F2] font-medium resize-none"
                  placeholder="Worum geht's?"
                  aria-invalid={!!errors.message}
                />
                {errors.message && (
                  <p data-testid="contact-message-error" className="mt-2 font-mono-acc text-[10px] text-[#FF3B00]">
                    {errors.message}
                  </p>
                )}
              </div>

              <div className="flex flex-wrap items-center justify-between gap-4 pt-2">
                <p className="font-mono-acc text-[10px] text-[#525252] max-w-xs">
                  Antwort meistens innerhalb von 48 Std.
                </p>
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-form-submit"
                  className="group inline-flex items-center gap-3 px-6 py-3 bg-[#FF3B00] text-white border-2 border-black shadow-brutal hover:translate-y-[2px] hover:shadow-brutal-press transition-all font-mono-acc text-xs disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {loading ? "Wird gesendet…" : sent ? "Erneut senden" : "Nachricht senden"}
                  <Send className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}
